import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import type {
  ConversationPlan,
  InformationNeed,
  PlanFinding,
  PlanSummary,
  PlanSupportingQuote,
  Sentiment,
} from '@knownow/domain'
import { Badge, Button } from '@knownow/ui'
import {
  downloadPdfBlob,
  fetchPlanSummaryPdf,
  generatePlanSummary,
  getPlanSummary,
} from '@/lib/summary-api-client'
import { PlanSummaryChart } from './plan-summary-charts'

export interface PlanSummaryTabProps {
  readonly plan: ConversationPlan
}

type Status = 'loading' | 'idle' | 'generating' | 'ready' | 'error'

interface TabState {
  status: Status
  summary: PlanSummary | null
  error: string | null
}

export function PlanSummaryTab({ plan }: PlanSummaryTabProps): React.ReactElement {
  const [state, setState] = useState<TabState>({
    status: 'loading',
    summary: null,
    error: null,
  })
  const [downloading, setDownloading] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getPlanSummary(plan.id)
      .then((summary) => {
        if (cancelled) return
        setState({
          status: summary ? 'ready' : 'idle',
          summary,
          error: null,
        })
      })
      .catch((err) => {
        if (cancelled) return
        setState({ status: 'error', summary: null, error: (err as Error).message })
      })
    return (): void => {
      cancelled = true
    }
  }, [plan.id])

  const runGenerate = useCallback(async () => {
    setState((s) => ({ ...s, status: 'generating', error: null }))
    try {
      const summary = await generatePlanSummary(plan.id)
      setState({ status: 'ready', summary, error: null })
    } catch (err) {
      setState((s) => ({
        ...s,
        status: 'error',
        error: (err as Error).message,
      }))
    }
  }, [plan.id])

  const runDownloadPdf = useCallback(async () => {
    setDownloading(true)
    setPdfError(null)
    try {
      const pdf = await fetchPlanSummaryPdf(plan.id)
      downloadPdfBlob(pdf)
    } catch (err) {
      // Surface the failure inline near the Download button rather than
      // clobbering the main tab state — the loaded summary should stay
      // visible so the user can retry without losing context.
      setPdfError((err as Error).message)
    } finally {
      setDownloading(false)
    }
  }, [plan.id])

  const activeNeeds = plan.informationNeeds.filter((n) => n.deletedAt === null)

  return (
    <div className="flex flex-col h-full" data-testid="plan-summary-tab">
      <Header
        state={state}
        onRun={runGenerate}
        onDownloadPdf={runDownloadPdf}
        downloading={downloading}
        pdfError={pdfError}
      />
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {state.status === 'loading' && (
          <p className="text-sm text-neutral-500">Loading summary…</p>
        )}
        {state.status === 'idle' && <IdleState onRun={runGenerate} />}
        {state.status === 'generating' && (
          <GeneratingSkeleton needs={activeNeeds} />
        )}
        {state.status === 'error' && (
          <ErrorState
            message={state.error ?? 'Something went wrong'}
            onRetry={runGenerate}
          />
        )}
        {state.status === 'ready' && state.summary && (
          <SummaryBody summary={state.summary} needs={activeNeeds} />
        )}
      </div>
    </div>
  )
}

function Header({
  state,
  onRun,
  onDownloadPdf,
  downloading,
  pdfError,
}: {
  state: TabState
  onRun: () => void
  onDownloadPdf: () => void
  downloading: boolean
  pdfError: string | null
}): React.ReactElement {
  const busy = state.status === 'generating'
  const hasSummary = state.summary !== null
  return (
    <div className="flex items-start justify-between border-b border-neutral-200 px-6 py-3 gap-4">
      <div>
        <h3 className="text-base font-semibold text-neutral-900">
          Plan summary
        </h3>
        {hasSummary && state.summary && (
          <p className="text-xs text-neutral-500 mt-0.5">
            Based on {state.summary.respondentCount} completed respondent
            {state.summary.respondentCount === 1 ? '' : 's'}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="flex gap-2">
        {hasSummary && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onDownloadPdf}
            loading={downloading}
            disabled={downloading || busy}
            data-testid="plan-summary-download-pdf"
          >
            Download as PDF
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={onRun}
          loading={busy}
          disabled={busy}
        >
          {hasSummary ? 'Regenerate' : 'Summarize'}
        </Button>
        </div>
        {pdfError && (
          <p
            className="text-xs text-error-dark"
            data-testid="plan-summary-pdf-error"
          >
            PDF download failed: {pdfError}
          </p>
        )}
      </div>
    </div>
  )
}

function IdleState({ onRun }: { onRun: () => void }): React.ReactElement {
  return (
    <div className="rounded-lg border border-dashed border-neutral-200 p-8 text-center">
      <p className="text-sm text-neutral-600 mb-3">
        Aggregate findings across every completed response. Qualitative
        answers are summarised with supporting quotes; quantitative answers
        are rendered as distributions.
      </p>
      <Button variant="primary" size="sm" onClick={onRun}>
        Summarize
      </Button>
    </div>
  )
}

function GeneratingSkeleton({
  needs,
}: {
  needs: readonly InformationNeed[]
}): React.ReactElement {
  return (
    <div className="space-y-3" data-testid="plan-summary-generating">
      <div className="h-20 rounded-lg bg-neutral-100 animate-pulse" />
      {needs.map((n) => (
        <div
          key={n.id}
          className="h-32 rounded-lg bg-neutral-100 animate-pulse"
        />
      ))}
    </div>
  )
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}): React.ReactElement {
  return (
    <div
      className="rounded-lg bg-error-light text-error-dark p-4 text-sm"
      data-testid="plan-summary-error"
    >
      <p className="mb-2">Summary failed: {message}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

function SummaryBody({
  summary,
  needs,
}: {
  summary: PlanSummary
  needs: readonly InformationNeed[]
}): React.ReactElement {
  const findingByNeedId = new Map(
    summary.findings.map((f) => [f.informationNeedId, f]),
  )
  return (
    <div className="space-y-4">
      <section
        className="rounded-lg border border-neutral-200 bg-neutral-0 p-5"
        data-testid="plan-summary-narrative"
      >
        <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">
          Overview
        </h4>
        <p className="text-sm text-neutral-800 leading-relaxed">
          {summary.narrative}
        </p>
      </section>

      {needs.map((need) => (
        <PlanNeedCard
          key={need.id}
          need={need}
          finding={findingByNeedId.get(need.id) ?? null}
        />
      ))}

      <p className="text-xs text-neutral-400">
        Generated {summary.generatedAt.toLocaleString()} · model{' '}
        {summary.modelVersion}
      </p>
    </div>
  )
}

function PlanNeedCard({
  need,
  finding,
}: {
  need: InformationNeed
  finding: PlanFinding | null
}): React.ReactElement {
  const priorityVariant =
    need.priority === 'high'
      ? 'error'
      : need.priority === 'low'
        ? 'neutral'
        : 'warning'
  const hasContent =
    finding !== null &&
    (finding.qualitativeSummary !== null ||
      finding.supportingQuotes.length > 0 ||
      finding.quantitativeAggregate !== null)
  return (
    <article
      className="rounded-lg border border-neutral-200 bg-neutral-0 p-5"
      data-testid={`plan-need-card-${need.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h4 className="text-sm font-medium text-neutral-800 leading-snug">
          {need.description}
        </h4>
        <div className="flex gap-1 shrink-0">
          <Badge variant={priorityVariant} size="sm">
            {need.priority}
          </Badge>
          <Badge variant="info" size="sm">
            {need.collectionFormat}
          </Badge>
        </div>
      </div>

      {finding?.qualitativeSummary && (
        <p className="text-sm text-neutral-700 leading-relaxed mb-3">
          {finding.qualitativeSummary}
        </p>
      )}

      {finding && finding.supportingQuotes.length > 0 && (
        <ul className="space-y-2 mb-3">
          {finding.supportingQuotes.map((q, i) => (
            <QuoteItem key={i} quote={q} />
          ))}
        </ul>
      )}

      {finding?.quantitativeAggregate && (
        <div className="rounded-md bg-neutral-50 p-4">
          <PlanSummaryChart aggregate={finding.quantitativeAggregate} />
        </div>
      )}

      {!hasContent && (
        <p className="text-sm text-neutral-400 italic">
          No responses addressed this need yet.
        </p>
      )}
    </article>
  )
}

function QuoteItem({
  quote,
}: {
  quote: PlanSupportingQuote
}): React.ReactElement {
  return (
    <li className="border-l-2 border-brand-300 ps-3">
      <p className="font-serif text-sm text-neutral-800 italic leading-relaxed">
        &ldquo;{quote.content}&rdquo;
      </p>
      <div className="mt-1 flex items-center gap-2 text-xs text-neutral-500">
        <span>Respondent {quote.respondentId.slice(0, 8)}</span>
        {quote.sentiment && (
          <Badge variant={sentimentVariant(quote.sentiment)} size="sm">
            {quote.sentiment}
          </Badge>
        )}
      </div>
    </li>
  )
}

function sentimentVariant(
  sentiment: Sentiment,
): 'success' | 'error' | 'warning' | 'neutral' {
  switch (sentiment) {
    case 'positive':
      return 'success'
    case 'negative':
      return 'error'
    case 'mixed':
      return 'warning'
    case 'neutral':
      return 'neutral'
  }
}
